import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Lock, Eye, Globe, Shield, Stethoscope, Tractor, Calculator, Cpu,
  BookOpen, Award, Sprout, TrendingUp, Users, Check,
  ChevronRight, ChevronLeft, Maximize, Minimize, Monitor,
  Building, Briefcase, DollarSign, Target, ArrowRight, ExternalLink,
  FileText, MapPin, Heart,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VOXIT_LOGO = 'https://customer-assets.emergentagent.com/job_8fab86eb-85cd-46bf-b407-6c1fe82b0eb3/artifacts/wcvtgo52_1.png';
const SME_LOGO = 'https://customer-assets.emergentagent.com/job_8fab86eb-85cd-46bf-b407-6c1fe82b0eb3/artifacts/z4hsx05x_image.png';

/* ═══════════════════════════════════════════
   COLOR PALETTE (light theme)
   ═══════════════════════════════════════════ */
const C = {
  bg: '#f8fafc',
  card: '#ffffff',
  border: '#e2e8f0',
  text: '#1e293b',
  muted: '#64748b',
  light: '#94a3b8',
  blue: '#2563eb',
  blueBg: '#eff6ff',
  blueBorder: '#bfdbfe',
  green: '#059669',
  greenBg: '#ecfdf5',
  greenBorder: '#a7f3d0',
  purple: '#7c3aed',
  purpleBg: '#f5f3ff',
  purpleBorder: '#c4b5fd',
  amber: '#d97706',
  amberBg: '#fffbeb',
  amberBorder: '#fcd34d',
  teal: '#0891b2',
  tealBg: '#ecfeff',
  tealBorder: '#a5f3fc',
  red: '#dc2626',
};

/* ═══════════════════════════════════════════
   PILLAR DATA
   ═══════════════════════════════════════════ */
const PILLARS = [
  {
    title: 'Capacity Building',
    icon: BookOpen,
    color: C.blue,
    bg: C.blueBg,
    border: C.blueBorder,
    description: 'Building digital skills and knowledge across key sectors through hands-on platforms and training programs.',
    solutions: [
      { product: 'Finovo', icon: Calculator, color: C.blue, text: 'Digitizes accounting for SMEs — builds financial management capacity. Free tier available for new businesses to start immediately.' },
      { product: 'TotalFarm', icon: Tractor, color: C.amber, text: 'Trains farmers in digital farm management with 24/7 agricultural specialist support and AI-powered guidance.' },
      { product: 'Care365', icon: Stethoscope, color: C.green, text: '22 qualified doctors already trained and onboarded across Lusaka, Chipata, Kitwe, and Livingstone.' },
      { product: 'SwiftGuard', icon: Shield, color: C.red, text: 'Emergency response training, protocols, and active ZNPHI call center integration for health emergencies.' },
    ],
    insight: 'Our platforms don\'t just provide tools — they provide training, onboarding support, and ongoing capacity development for every user.',
  },
  {
    title: 'SME Development Exhibitions & Trade Fairs',
    icon: Award,
    color: C.amber,
    bg: C.amberBg,
    border: C.amberBorder,
    description: 'We are ready to participate in and contribute to exhibitions, fairs, and events that showcase digital transformation for SMEs.',
    solutions: [
      { product: 'Cosmetology Fair', icon: Heart, color: C.green, text: 'Care365 health-tech showcase — demonstrate telemedicine and digital health tools to beauty and wellness SMEs.' },
      { product: 'SME Awards Gala Dinner', icon: Award, color: C.amber, text: 'Showcase the full VoxIT digital suite — Finovo, TotalFarm, Care365, SwiftGuard — as success stories in SME innovation.' },
      { product: 'Finance Fair', icon: Calculator, color: C.blue, text: 'Live Finovo accounting demonstrations — show SMEs how to automate bookkeeping, ZRA Smart Invoice, and financial compliance.' },
      { product: 'UN MSME Day & Cooperatives Day', icon: Globe, color: C.purple, text: '"SMEs Going Digital" showcase — TotalFarm for cooperatives, Finovo for MSMEs, full digital transformation demos.' },
    ],
    insight: 'We bring interactive product demos, live showcases, success stories, and professional booth materials to every event.',
  },
  {
    title: 'Rural Enterprise Development',
    icon: Sprout,
    color: C.green,
    bg: C.greenBg,
    border: C.greenBorder,
    description: 'All our platforms are designed to work in low-bandwidth environments, bridging the digital divide for rural SMEs.',
    solutions: [
      { product: 'Smart Farming Box', icon: Cpu, color: C.green, text: 'IoT precision agriculture for rural farmers — real-time soil monitoring, AI crop disease detection, weather alerts.' },
      { product: 'TotalFarm', icon: Tractor, color: C.amber, text: 'Complete farm management with Mobile Money payments (MTN & Airtel) — designed for rural operations.' },
      { product: 'Care365', icon: Stethoscope, color: C.green, text: 'Telemedicine reaching rural communities — 22 doctors accessible via phone from anywhere in Zambia.' },
      { product: 'SwiftGuard', icon: Shield, color: C.red, text: 'Emergency SOS alerts optimized for low-bandwidth areas — ensuring rural safety and rapid response.' },
    ],
    insight: 'Every platform we build is optimized for low-bandwidth and rural access — because digital transformation must reach every Zambian.',
  },
  {
    title: 'Publication of Market Intelligence',
    icon: TrendingUp,
    color: C.purple,
    bg: C.purpleBg,
    border: C.purpleBorder,
    description: 'Our active platforms generate real-time, ground-level data that transforms into actionable market intelligence.',
    solutions: [
      { product: 'Agricultural Insights', icon: Sprout, color: C.green, text: 'Smart Farming Box soil analytics + TotalFarm crop yields and farming patterns — real data from active Zambian farms.' },
      { product: 'Health Sector Data', icon: Stethoscope, color: C.green, text: 'Care365 consultation patterns, access metrics, and rural health coverage data across 4 cities.' },
      { product: 'Financial Trends', icon: Calculator, color: C.blue, text: 'Finovo SME bookkeeping trends, digital adoption rates, and ZRA compliance patterns.' },
      { product: 'Security Intelligence', icon: Shield, color: C.red, text: 'SwiftGuard emergency response times, incident patterns, and public health surveillance data via ZNPHI.' },
    ],
    insight: 'We propose co-publishing quarterly "Digital SME Intelligence" reports — real-time, actionable data from the ground, not surveys.',
  },
  {
    title: 'MSME Support & Programme Development',
    icon: Users,
    color: C.teal,
    bg: C.tealBg,
    border: C.tealBorder,
    description: 'A complete digital toolkit to support MSME growth, digitization, and government program integration.',
    solutions: [
      { product: 'Finovo as Official SME Tool', icon: Calculator, color: C.blue, text: 'Recommended digital accounting platform for all registered SMEs — free tier, ZRA integration, government-ready.' },
      { product: 'SME Digital Starter Pack', icon: Briefcase, color: C.teal, text: 'Bundled access: Finovo + TotalFarm + Care365 — a complete digital transformation package for new MSMEs.' },
      { product: 'Government Integration', icon: Building, color: C.purple, text: 'Connect with SME registration systems — track digitization progress, monitor adoption, measure impact.' },
      { product: 'Training & Mentorship', icon: BookOpen, color: C.amber, text: 'Platform-powered mentorship programs — use real data to guide SMEs through digital transformation journeys.' },
    ],
    insight: 'Together, we can build Zambia\'s most comprehensive MSME digital support ecosystem — measurable, scalable, and self-sustaining.',
  },
];

/* ═══════════════════════════════════════════
   PROMOTION CHANNELS
   ═══════════════════════════════════════════ */
const CHANNELS = [
  { title: 'Market Intelligence Publication', status: 'Ready', icon: FileText, color: C.blue, description: 'Ready to advertise our products and contribute real-time platform data for your upcoming publication. Our active platforms generate actionable insights across agriculture, health, finance, and security sectors.' },
  { title: 'Sector-Specific Engagement', status: 'Ready', icon: Users, color: C.green, description: 'Prepared for meetings with key decision-makers in security and health sectors. Ready to host webinars demonstrating our products\' value proposition to a broader Zambian audience.' },
  { title: 'Strategic Partnerships', status: 'Active', icon: Building, color: C.purple, description: 'MOU sent to ZARI and ZNPHI. In discussion with PDU. Ready to be connected with ZRA, NHIMA, DMMU, Ministry of Health, Ministry of Agriculture, Ministry of Technology & Science, and ZICTA through your facilitation.' },
  { title: 'Digital Showcase: "SMEs Going Digital"', status: 'Perfect Fit', icon: Monitor, color: C.amber, description: '"SMEs Going Digital" aligns perfectly with every platform we\'ve built. We\'re ready for live product demos, interactive showcases, and hands-on training sessions at any venue.' },
];

/* ═══════════════════════════════════════════
   GOVERNMENT PARTNERSHIPS
   ═══════════════════════════════════════════ */
const PARTNERSHIPS = [
  { name: 'ZRA', full: 'Zambia Revenue Authority', status: 'Ready to be Connected', product: 'Finovo Smart Invoice', color: C.blue },
  { name: 'ZARI', full: 'Agricultural Research Institute', status: 'MOU Sent', product: 'Smart Farming Box', color: C.green },
  { name: 'ZNPHI', full: 'National Public Health Institute', status: 'MOU Sent — Not Moving Forward', product: 'SwiftGuard — Ready to move on our own', color: C.red },
  { name: 'NHIMA', full: 'National Health Insurance', status: 'Ready to be Connected', product: 'Care365', color: C.purple },
  { name: 'PDU', full: 'Presidential Delivery Unit', status: 'In Discussion', product: 'Smart Farming Box', color: C.green },
  { name: 'DMMU', full: 'Disaster Management & Mitigation Unit', status: 'Ready to be Connected', product: 'SwiftGuard / Dictor Platform', color: C.red },
  { name: 'Min. of Health', full: '', status: 'Ready to be Connected', product: 'Care365 & SwiftGuard', color: C.green },
  { name: 'Min. of Agriculture', full: '', status: 'Ready to be Connected', product: 'Smart Farming Box & TotalFarm', color: C.amber },
  { name: 'Min. of Technology', full: '', status: 'Ready to be Connected', product: 'All Platforms', color: C.teal },
  { name: 'ZICTA', full: 'ICT Authority', status: 'Ready to be Connected', product: 'Digital Compliance', color: C.light },
];

/* ═══════════════════════════════════════════
   ROADMAP
   ═══════════════════════════════════════════ */
const ROADMAP = [
  { quarter: 'Q1', title: 'Foundation', color: C.blue, items: ['Formal partnership MOU signing', 'Finovo SME pilot program launch', 'Joint press release and announcement'] },
  { quarter: 'Q2', title: 'Visibility', color: C.purple, items: ['Trade fair & exhibition participation', 'Webinar series: "SMEs Going Digital"', 'Smart Farming Box live demonstrations'] },
  { quarter: 'Q3', title: 'Scale', color: C.green, items: ['Market Intelligence co-publication', 'Care365 rural outreach program', 'SME Digital Starter Pack rollout'] },
  { quarter: 'Q4', title: 'Impact & Growth', color: C.amber, items: ['Comprehensive impact assessment', 'Scale-up planning for successful programs', 'Annual review and expansion strategy'] },
];

/* ═══════════════════════════════════════════
   SLIDE LABELS
   ═══════════════════════════════════════════ */
const SLIDE_LABELS = [
  'Cover', 'About VoxIT', 'Your 5 Pillars',
  'Capacity Building', 'Exhibitions & Fairs', 'Rural Enterprise', 'Market Intelligence', 'MSME Support',
  'Promotion Channels', 'Gov. Partnerships', 'Revenue & Reinvestment', 'Collaboration Roadmap', 'Next Steps',
];
const TOTAL_SLIDES = SLIDE_LABELS.length;

/* ═══════════════════════════════════════════
   JOB CREATION STATS
   ═══════════════════════════════════════════ */
const JOBS = [
  { product: 'SwiftGuard', roles: 'Call Center Support', count: '6-8', color: C.red },
  { product: 'Care365', roles: 'Doctors & Medical Staff', count: '21+', color: C.green },
  { product: 'Smart Farming Box', roles: 'Phone Support', count: '5', color: C.green },
  { product: 'Finovo', roles: 'Accountant Assistants', count: 'Up to 15', color: C.blue },
  { product: 'TotalFarm', roles: 'Support & Operations', count: '3-4', color: C.amber },
];

/* ═══════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════ */
const SMEPartnershipPage = () => {
  const [phase, setPhase] = useState('viewing');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  const goNext = useCallback(() => setCurrentSlide(s => Math.min(TOTAL_SLIDES - 1, s + 1)), []);
  const goPrev = useCallback(() => setCurrentSlide(s => Math.max(0, s - 1)), []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  useEffect(() => {
    const h = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', h);
    return () => document.removeEventListener('fullscreenchange', h);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goNext();
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') goPrev();
      else if (e.key === 'f' || e.key === 'F') toggleFullscreen();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [goNext, goPrev, toggleFullscreen]);

  /* ─── Presentation Viewer ─── */
  const renderSlide = () => {
    switch (currentSlide) {
      case 0: return <CoverSlide />;
      case 1: return <AboutSlide />;
      case 2: return <PillarOverviewSlide onPillarClick={(i) => setCurrentSlide(i + 3)} />;
      case 3: case 4: case 5: case 6: case 7:
        return <PillarDetailSlide pillar={PILLARS[currentSlide - 3]} index={currentSlide - 3} />;
      case 8: return <ChannelsSlide />;
      case 9: return <PartnershipsSlide />;
      case 10: return <RevenueSlide />;
      case 11: return <RoadmapSlide />;
      case 12: return <ThankYouSlide />;
      default: return null;
    }
  };

  return (
    <div ref={containerRef} data-testid="sme-presentation" className="min-h-screen flex flex-col" style={{ background: C.bg }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-50 px-5 py-3 flex items-center justify-between bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-3">
          <img src={VOXIT_LOGO} alt="VoxIT" className="h-7 object-contain" />
          <span className="text-xs" style={{ color: C.light }}>|</span>
          <span className="text-sm font-semibold hidden sm:block" style={{ color: C.text }}>VoxIT x Department of SME</span>
          <span className="text-xs px-2.5 py-1 rounded-full font-medium" style={{ background: C.blueBg, color: C.blue, border: `1px solid ${C.blueBorder}` }}>
            {SLIDE_LABELS[currentSlide]}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium" style={{ color: C.muted }}>{currentSlide + 1} / {TOTAL_SLIDES}</span>
          <button data-testid="sme-fullscreen-btn" onClick={toggleFullscreen} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:brightness-95" style={{ background: C.blueBg, color: C.blue, border: `1px solid ${C.blueBorder}` }}>
            {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="h-1 w-full" style={{ background: '#e2e8f0' }}>
        <motion.div animate={{ width: `${((currentSlide + 1) / TOTAL_SLIDES) * 100}%` }} className="h-full" style={{ background: `linear-gradient(90deg, ${C.blue}, ${C.purple})` }} />
      </div>

      {/* Slide Content */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
        <AnimatePresence mode="wait">
          <motion.div key={currentSlide} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
            {renderSlide()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Navigation */}
      <div className="sticky bottom-0 z-50 px-5 py-3 flex items-center justify-between bg-white" style={{ borderTop: `1px solid ${C.border}` }}>
        <button data-testid="sme-prev-btn" onClick={goPrev} disabled={currentSlide === 0}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium disabled:opacity-30 transition-all hover:brightness-95"
          style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text }}>
          <ChevronLeft size={16} /> Previous
        </button>
        {/* Mini nav dots */}
        <div className="hidden sm:flex items-center gap-1">
          {SLIDE_LABELS.map((_, i) => (
            <button key={i} onClick={() => setCurrentSlide(i)} className="w-2 h-2 rounded-full transition-all"
              style={{ background: i === currentSlide ? C.blue : '#cbd5e1', transform: i === currentSlide ? 'scale(1.5)' : 'scale(1)' }} />
          ))}
        </div>
        <button data-testid="sme-next-btn" onClick={goNext} disabled={currentSlide === TOTAL_SLIDES - 1}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-30 transition-all hover:brightness-110"
          style={{ background: C.blue }}>
          Next <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SLIDE: COVER
   ═══════════════════════════════════════════ */
const CoverSlide = () => (
  <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6 py-16" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #f8fafc 50%, #ecfdf5 100%)' }}>
    <div className="text-center max-w-3xl">
      {/* Logos */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="flex items-center justify-center gap-6 mb-10">
        <img src={VOXIT_LOGO} alt="VoxIT Consulting" className="h-14 sm:h-16 object-contain" />
        <div className="w-px h-12" style={{ background: C.border }} />
        <img src={SME_LOGO} alt="Ministry of SME Development" className="h-16 sm:h-20 object-contain" />
      </motion.div>

      <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight" style={{ color: C.text }}>
        Strategic Technology<br />Partnership Proposal
      </motion.h1>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: C.blueBg, border: `1px solid ${C.blueBorder}` }}>
        <Globe size={14} style={{ color: C.blue }} />
        <span className="text-sm font-medium" style={{ color: C.blue }}>Confidential Partnership Proposal</span>
      </motion.div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
        className="text-base mb-10 max-w-xl mx-auto leading-relaxed" style={{ color: C.muted }}>
        Empowering Zambian SMEs Through Digital Transformation — A Revenue-Based Partnership for Mutual Growth
      </motion.p>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
        className="flex flex-wrap items-center justify-center gap-4">
        {[
          { label: '5 Active Platforms', icon: Monitor },
          { label: '50+ Jobs Created', icon: Users },
          { label: 'Revenue-Based', icon: DollarSign },
          { label: 'Reinvesting in Zambia', icon: MapPin },
        ].map(s => (
          <div key={s.label} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <s.icon size={16} style={{ color: C.blue }} />
            <span className="text-sm font-medium" style={{ color: C.text }}>{s.label}</span>
          </div>
        ))}
      </motion.div>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
        className="text-xs mt-12" style={{ color: C.light }}>
        Prepared for Pamela and the Department of SME Development, Republic of Zambia
      </motion.p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: ABOUT VOXIT
   ═══════════════════════════════════════════ */
const AboutSlide = () => (
  <div className="max-w-5xl mx-auto px-6 py-12">
    <SlideBadge icon={Briefcase} label="Who We Are" color={C.blue} bg={C.blueBg} border={C.blueBorder} />
    <div className="flex items-center gap-4 mt-4 mb-3">
      <img src={VOXIT_LOGO} alt="VoxIT" className="h-10 object-contain" />
      <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: C.text }}>VoxIT Consulting & Innovage Ltd</h2>
    </div>
    <p className="text-base leading-relaxed mb-8 max-w-3xl" style={{ color: C.muted }}>
      We are a revenue-generating technology company headquartered in Sweden with deep operations in Zambia. Our mission is to build and deploy digital platforms that solve real challenges across healthcare, public safety, agriculture, and finance — with all revenue reinvested directly into Zambian growth and job creation.
    </p>

    {/* Stats Grid */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
      {[
        { value: '5', label: 'Active Platforms', sub: 'Serving Zambian markets', color: C.blue },
        { value: '50+', label: 'Jobs Created', sub: 'Across all operations', color: C.green },
        { value: '4+', label: 'Cities Covered', sub: 'Lusaka, Chipata, Kitwe, Livingstone', color: C.purple },
        { value: '22', label: 'Doctors Onboarded', sub: 'Ready for Care365 launch', color: C.amber },
      ].map(s => (
        <div key={s.label} className="p-5 rounded-xl bg-white text-center" style={{ border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <div className="text-3xl font-bold mb-1" style={{ color: s.color }}>{s.value}</div>
          <div className="text-sm font-semibold mb-0.5" style={{ color: C.text }}>{s.label}</div>
          <div className="text-xs" style={{ color: C.light }}>{s.sub}</div>
        </div>
      ))}
    </div>

    {/* Products Preview */}
    <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: C.muted }}>Our Platform Portfolio</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {[
        { name: 'SwiftGuard', icon: Shield, tag: 'Emergency Response', color: C.red, url: 'swiftguard.app' },
        { name: 'Care365', icon: Stethoscope, tag: 'Telemedicine', color: C.green, url: 'care365.clinic' },
        { name: 'Smart Farming Box', icon: Cpu, tag: 'IoT Agriculture', color: C.green, url: null },
        { name: 'Finovo', icon: Calculator, tag: 'AI Accounting', color: C.blue, url: 'finovoapp.com' },
        { name: 'TotalFarm', icon: Tractor, tag: 'Farm Management', color: C.amber, url: 'totalfarm.pro' },
      ].map(p => (
        <div key={p.name} className="p-4 rounded-xl bg-white flex flex-col items-center text-center" style={{ border: `1px solid ${C.border}` }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-2" style={{ background: `${p.color}15` }}>
            <p.icon size={20} style={{ color: p.color }} />
          </div>
          <div className="text-sm font-semibold" style={{ color: C.text }}>{p.name}</div>
          <div className="text-xs mb-2" style={{ color: C.light }}>{p.tag}</div>
          {p.url && (
            <a href={`https://${p.url}`} target="_blank" rel="noopener noreferrer" className="text-xs flex items-center gap-1 hover:underline" style={{ color: C.blue }}>
              {p.url} <ExternalLink size={10} />
            </a>
          )}
        </div>
      ))}
    </div>

    <div className="mt-8 p-5 rounded-xl" style={{ background: C.blueBg, border: `1px solid ${C.blueBorder}` }}>
      <p className="text-sm font-medium leading-relaxed" style={{ color: C.blue }}>
        We are not grant-dependent. VoxIT is a revenue-based company — every partnership drives mutual economic growth, and profits are reinvested directly into Zambian operations, jobs, and infrastructure.
      </p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: PILLAR OVERVIEW
   ═══════════════════════════════════════════ */
const PillarOverviewSlide = ({ onPillarClick }) => (
  <div className="max-w-5xl mx-auto px-6 py-12">
    <SlideBadge icon={Target} label="Strategic Alignment" color={C.purple} bg={C.purpleBg} border={C.purpleBorder} />
    <h2 className="text-2xl sm:text-3xl font-bold mb-3 mt-4" style={{ color: C.text }}>Your 5 Pillars — Our Digital Solutions</h2>
    <p className="text-base mb-10 max-w-3xl leading-relaxed" style={{ color: C.muted }}>
      Your Department's focus areas align directly with our technology portfolio. Here's how each of your 5 pillars maps to our active platforms and capabilities.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {PILLARS.map((p, i) => (
        <button key={i} onClick={() => onPillarClick(i)}
          className="p-5 rounded-xl text-left transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer bg-white"
          style={{ border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${p.color}15` }}>
              <p.icon size={20} style={{ color: p.color }} />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider" style={{ color: p.color }}>Pillar {i + 1}</div>
          </div>
          <h3 className="text-sm font-bold mb-2" style={{ color: C.text }}>{p.title}</h3>
          <p className="text-xs leading-relaxed mb-3" style={{ color: C.muted }}>{p.description}</p>
          <div className="flex items-center gap-1 text-xs font-medium" style={{ color: p.color }}>
            View details <ArrowRight size={12} />
          </div>
        </button>
      ))}

      {/* Summary card */}
      <div className="p-5 rounded-xl sm:col-span-2 lg:col-span-1" style={{ background: 'linear-gradient(135deg, #eff6ff, #ecfdf5)', border: `1px solid ${C.border}` }}>
        <div className="text-sm font-bold mb-3" style={{ color: C.text }}>Complete Alignment</div>
        <p className="text-xs leading-relaxed mb-4" style={{ color: C.muted }}>
          All 5 pillars of your Department map directly to one or more of our active platforms. This isn't theoretical — these are live, revenue-generating products with real users and measurable impact.
        </p>
        <div className="flex flex-wrap gap-2">
          {['SwiftGuard', 'Care365', 'Finovo', 'TotalFarm', 'Smart Farming Box'].map(n => (
            <span key={n} className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'white', color: C.text, border: `1px solid ${C.border}` }}>{n}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: PILLAR DETAIL (reusable)
   ═══════════════════════════════════════════ */
const PillarDetailSlide = ({ pillar, index }) => (
  <div className="max-w-5xl mx-auto px-6 py-12">
    <SlideBadge icon={pillar.icon} label={`Pillar ${index + 1} of 5`} color={pillar.color} bg={pillar.bg} border={pillar.border} />
    <h2 className="text-2xl sm:text-3xl font-bold mb-2 mt-4" style={{ color: C.text }}>{pillar.title}</h2>
    <p className="text-base mb-8 max-w-3xl leading-relaxed" style={{ color: C.muted }}>{pillar.description}</p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      {pillar.solutions.map((s, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
          className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderLeft: `3px solid ${s.color}` }}>
          <div className="flex items-center gap-2.5 mb-2">
            <s.icon size={18} style={{ color: s.color }} />
            <span className="text-sm font-bold" style={{ color: C.text }}>{s.product}</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: C.muted }}>{s.text}</p>
        </motion.div>
      ))}
    </div>

    <div className="p-5 rounded-xl" style={{ background: pillar.bg, border: `1px solid ${pillar.border}` }}>
      <div className="flex items-start gap-2.5">
        <Check size={18} className="flex-shrink-0 mt-0.5" style={{ color: pillar.color }} />
        <p className="text-sm font-medium leading-relaxed" style={{ color: pillar.color }}>{pillar.insight}</p>
      </div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: PROMOTION CHANNELS
   ═══════════════════════════════════════════ */
const ChannelsSlide = () => (
  <div className="max-w-5xl mx-auto px-6 py-12">
    <SlideBadge icon={Monitor} label="Your Channels — Our Readiness" color={C.green} bg={C.greenBg} border={C.greenBorder} />
    <h2 className="text-2xl sm:text-3xl font-bold mb-3 mt-4" style={{ color: C.text }}>Responding to Your Promotion Channels</h2>
    <p className="text-base mb-8 max-w-3xl leading-relaxed" style={{ color: C.muted }}>
      You identified 4 key promotion channels. We are ready for all of them — here's our status and what we bring to each.
    </p>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {CHANNELS.map((ch, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
          className="p-6 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${ch.color}15` }}>
                <ch.icon size={18} style={{ color: ch.color }} />
              </div>
              <span className="text-sm font-bold" style={{ color: C.text }}>{ch.title}</span>
            </div>
            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: C.greenBg, color: C.green, border: `1px solid ${C.greenBorder}` }}>
              {ch.status}
            </span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: C.muted }}>{ch.description}</p>
        </motion.div>
      ))}
    </div>

    <div className="mt-6 p-5 rounded-xl text-center" style={{ background: C.greenBg, border: `1px solid ${C.greenBorder}` }}>
      <p className="text-sm font-medium" style={{ color: C.green }}>
        We are also ready to host webinars, provide content for publications, and participate in any format that showcases digital transformation for SMEs.
      </p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: GOVERNMENT PARTNERSHIPS
   ═══════════════════════════════════════════ */
const PartnershipsSlide = () => (
  <div className="max-w-5xl mx-auto px-6 py-12">
    <SlideBadge icon={Building} label="Government Ecosystem" color={C.purple} bg={C.purpleBg} border={C.purpleBorder} />
    <h2 className="text-2xl sm:text-3xl font-bold mb-3 mt-4" style={{ color: C.text }}>Strategic Government Partnerships</h2>
    <p className="text-base mb-8 max-w-3xl leading-relaxed" style={{ color: C.muted }}>
      We have initiated contact with several Zambian institutions. Your facilitation can help us connect with the ones we haven't reached yet and accelerate the ones already in motion.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      {PARTNERSHIPS.map((p, i) => {
        const statusColor = p.status.includes('MOU Sent') ? C.amber
          : p.status === 'In Discussion' ? C.blue
          : p.status.includes('Not Moving Forward') ? C.red
          : C.light;
        return (
          <motion.div key={i} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="p-4 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderTop: `3px solid ${p.color}` }}>
            <div className="text-sm font-bold mb-1" style={{ color: C.text }}>{p.name}</div>
            {p.full && <div className="text-xs mb-2" style={{ color: C.light }}>{p.full}</div>}
            <span className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-2" style={{ background: `${statusColor}15`, color: statusColor }}>
              {p.status}
            </span>
            <div className="text-xs" style={{ color: C.muted }}>{p.product}</div>
          </motion.div>
        );
      })}
    </div>

    <div className="mt-6 p-5 rounded-xl" style={{ background: C.purpleBg, border: `1px solid ${C.purpleBorder}` }}>
      <p className="text-sm font-medium" style={{ color: C.purple }}>
        With your connections to ZRA, NHIMA, DMMU, Ministry of Health, Ministry of Agriculture, Ministry of Technology & Science, and ZICTA — we can open doors that accelerate these partnerships and create real impact.
      </p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: REVENUE & REINVESTMENT
   ═══════════════════════════════════════════ */
const RevenueSlide = () => (
  <div className="max-w-5xl mx-auto px-6 py-12">
    <SlideBadge icon={DollarSign} label="Business Model" color={C.green} bg={C.greenBg} border={C.greenBorder} />
    <h2 className="text-2xl sm:text-3xl font-bold mb-3 mt-4" style={{ color: C.text }}>Revenue-Based Partnership — Investing in Zambia</h2>
    <p className="text-base mb-8 max-w-3xl leading-relaxed" style={{ color: C.muted }}>
      We are not grant-dependent. Every platform generates revenue that is reinvested directly into Zambian operations, jobs, and infrastructure.
    </p>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      {/* Revenue Streams */}
      <div className="p-6 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: C.blue }}>Revenue Streams</h3>
        {[
          'SaaS subscriptions (Finovo, TotalFarm, Care365)',
          'Hardware sales (Smart Farming Box sensors)',
          'Government & institutional licensing (SwiftGuard)',
          'Consultation fees (Care365 doctors)',
          'Data analytics services for institutions',
          'Vendor marketplace commissions (TotalFarm)',
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2.5 mb-2.5">
            <Check size={15} className="flex-shrink-0 mt-0.5" style={{ color: C.green }} />
            <span className="text-sm" style={{ color: C.muted }}>{item}</span>
          </div>
        ))}
      </div>

      {/* Reinvestment */}
      <div className="p-6 rounded-xl bg-white" style={{ border: `1px solid ${C.border}` }}>
        <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: C.green }}>Reinvestment in Zambia</h3>
        {[
          'Local hiring across 4+ cities',
          'Training programs for doctors, farmers, support staff',
          'Infrastructure: servers, offices, operations',
          'Continued R&D on Zambia-focused solutions',
          'Community engagement and outreach programs',
          'Technology transfer and skill development',
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2.5 mb-2.5">
            <Check size={15} className="flex-shrink-0 mt-0.5" style={{ color: C.green }} />
            <span className="text-sm" style={{ color: C.muted }}>{item}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Job Creation */}
    <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: C.text }}>Direct Job Creation Across Platforms</h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
      {JOBS.map(j => (
        <div key={j.product} className="p-4 rounded-xl bg-white text-center" style={{ border: `1px solid ${C.border}`, borderBottom: `3px solid ${j.color}` }}>
          <div className="text-xl font-bold mb-1" style={{ color: j.color }}>{j.count}</div>
          <div className="text-xs font-semibold mb-0.5" style={{ color: C.text }}>{j.roles}</div>
          <div className="text-xs" style={{ color: C.light }}>{j.product}</div>
        </div>
      ))}
    </div>

    <div className="p-4 rounded-xl text-center" style={{ background: C.greenBg, border: `1px solid ${C.greenBorder}` }}>
      <p className="text-sm font-semibold" style={{ color: C.green }}>
        Every partnership with VoxIT directly creates Zambian jobs and drives local economic growth.
      </p>
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: COLLABORATION ROADMAP
   ═══════════════════════════════════════════ */
const RoadmapSlide = () => (
  <div className="max-w-5xl mx-auto px-6 py-12">
    <SlideBadge icon={Target} label="Proposed Timeline" color={C.amber} bg={C.amberBg} border={C.amberBorder} />
    <h2 className="text-2xl sm:text-3xl font-bold mb-3 mt-4" style={{ color: C.text }}>Proposed Collaboration Roadmap</h2>
    <p className="text-base mb-10 max-w-3xl leading-relaxed" style={{ color: C.muted }}>
      A phased approach to building our partnership — starting with quick wins and scaling based on measurable impact.
    </p>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
      {ROADMAP.map((phase, i) => (
        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
          className="p-5 rounded-xl bg-white" style={{ border: `1px solid ${C.border}`, borderTop: `3px solid ${phase.color}` }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: phase.color }}>{phase.quarter}</span>
            <span className="text-sm font-bold" style={{ color: C.text }}>{phase.title}</span>
          </div>
          {phase.items.map((item, j) => (
            <div key={j} className="flex items-start gap-2 mb-2">
              <ChevronRight size={13} className="flex-shrink-0 mt-0.5" style={{ color: phase.color }} />
              <span className="text-xs leading-relaxed" style={{ color: C.muted }}>{item}</span>
            </div>
          ))}
        </motion.div>
      ))}
    </div>

    {/* Success Indicators */}
    <h3 className="text-sm font-bold uppercase tracking-wider mb-4" style={{ color: C.text }}>Success Indicators We'll Track Together</h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {[
        'SMEs Digitized', 'Jobs Created', 'Revenue Generated', 'Reports Published', 'Events Participated',
      ].map(ind => (
        <div key={ind} className="p-3 rounded-xl bg-white text-center" style={{ border: `1px solid ${C.border}` }}>
          <Check size={16} className="mx-auto mb-1" style={{ color: C.green }} />
          <span className="text-xs font-medium" style={{ color: C.text }}>{ind}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ═══════════════════════════════════════════
   SLIDE: THANK YOU
   ═══════════════════════════════════════════ */
const ThankYouSlide = () => {
  const navigate = useNavigate();
  return (
  <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6 py-16" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #f8fafc 50%, #ecfdf5 100%)' }}>
    <div className="text-center max-w-2xl">
      {/* Partner Logos */}
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 }}
        className="flex items-center justify-center gap-5 mb-8">
        <img src={VOXIT_LOGO} alt="VoxIT" className="h-12 object-contain" />
        <div className="w-px h-10" style={{ background: C.border }} />
        <img src={SME_LOGO} alt="Ministry of SME" className="h-14 object-contain" />
      </motion.div>

      <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: C.text }}>
        Let's Build Zambia's Digital<br />Future Together
      </motion.h2>

      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
        className="text-base mb-8 leading-relaxed" style={{ color: C.muted }}>
        We're excited about the alignment between your Department's mission and our technology platforms. Together, we can drive meaningful SME growth, create jobs, and build a digitally empowered Zambia.
      </motion.p>

      {/* Workspace CTA */}
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
        <button data-testid="go-to-workspace-btn" onClick={() => navigate('/sme-workspace')}
          className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-white text-base transition-all hover:brightness-110 hover:scale-[1.02]" style={{ background: C.blue, boxShadow: '0 4px 20px rgba(37,99,235,0.3)' }}>
          <Briefcase size={20} /> Open Collaboration Workspace <ArrowRight size={18} />
        </button>
        <p className="text-xs mt-3" style={{ color: C.muted }}>Track initiatives, action items, and requirements together</p>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="mt-8">
        <p className="text-sm font-medium mb-2" style={{ color: C.text }}>Contact Us</p>
        <p className="text-sm" style={{ color: C.blue }}>sales@voxitconsulting.media</p>
        <a href="https://voxitconsulting.media" target="_blank" rel="noopener noreferrer" className="text-sm flex items-center justify-center gap-1 mt-1 hover:underline" style={{ color: C.blue }}>
          voxitconsulting.media <ExternalLink size={12} />
        </a>
      </motion.div>
    </div>
  </div>
  );
};

/* ═══════════════════════════════════════════
   SHARED COMPONENTS
   ═══════════════════════════════════════════ */
const SlideBadge = ({ icon: Icon, label, color, bg, border }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: bg, border: `1px solid ${border}` }}>
    <Icon size={14} style={{ color }} />
    <span className="text-xs font-medium" style={{ color }}>{label}</span>
  </div>
);

export default SMEPartnershipPage;
